import _ from "lodash";
import { connect, useSelector } from "react-redux";
import { Form } from "formik";
import React, { useEffect, useState } from "react";
import Recaptcha from "react-recaptcha";
import * as yup from "yup";

import { Button, ButtonTypes } from "@/component-library";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { CustomForm } from "@/component-library/CustomForm";
import { FeatureFlagEnum } from "@/features/feature-flag/constants";
import { FormFieldError } from "@/component-library/FormFields/FormFieldError/FormFieldError";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { publicRequests } from "@/api/(requests)/public";
import { showAlertWithAction, showAlertWithCustomHTML } from "@/redux/actions/alerts";

import "./styles.scss";

const HCPInfo = ({ currentStepName, update_state_and_get_next_node }) => {
  let reCaptchaRef = React.createRef();

  const [mounted, setMounted] = useState(false);

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [defaultPracticeId, setDefaultPracticeId] = useState(false);
  const publicFlags = useSelector((state) => state.flags.publicFlags);
  const publicFlagsHaveLoaded = useSelector((state) => state.flags.publicFlagsHaveLoaded);

  useEffect(() => {
    setTimeout(() => {
      setMounted(true);
    }, 500);
  }, []);

  useEffect(() => {
    (async () => {
      const default_practice_id = await publicRequests
        .get_config({ searchParams: { feature_name: "HCP_DEFAULT_PRACTICE" } })
        .then((result) => result.practice_id);
      const practices = await publicRequests.get_all_hcp_practices();
      const filteredPractices = practices.filter((p) => p.id !== default_practice_id);
      const selectOptions = filteredPractices.map((practice) => {
        return {
          label: practice.name,
          value: practice.id,
        };
      });

      selectOptions.sort((a, b) => a.label.localeCompare(b.label));
      selectOptions.push({ label: "Other", value: default_practice_id });
      setDefaultPracticeId(default_practice_id);
      setOrganizationOptions(selectOptions);
    })();
  }, []);

  const renderForm = (formikProps) => {
    if (
      publicFlagsHaveLoaded &&
      publicFlags?.[FeatureFlagEnum.recaptchaOverride] &&
      !formikProps.values.recaptcha
    ) {
      formikProps.setFieldValue("recaptcha", true);
    }

    return (
      <Form className="HCPWorkflowShared-form">
        <div className="HCPWorkflowShared-form_fields">
          <FormTextInput
            className="HCPWorkflowShared-form_field"
            formikProps={formikProps}
            name="first_name"
            title="Your First Name"
            triggerErrorOnChange
          />

          <FormTextInput
            className="HCPWorkflowShared-form_field"
            formikProps={formikProps}
            name="last_name"
            title="Your Last Name"
            triggerErrorOnChange
          />

          <FormTextInput
            className="HCPWorkflowShared-form_field"
            formikProps={formikProps}
            name="email"
            title="Email"
            triggerErrorOnChange
          />

          <FormTextInput
            className="HCPWorkflowShared-form_field"
            formikProps={formikProps}
            name="email_validation"
            title="Confirm Email"
            triggerErrorOnChange
          />

          <FormSelect
            className="HCPWorkflowShared-form_field"
            formikProps={formikProps}
            name="organization"
            options={organizationOptions}
            placeholder="Search Organization"
            testId="HCPOrganizationDropdown"
            title="Your Organization"
            triggerErrorOnChange
          />

          <FormSelect
            className="HCPWorkflowShared-form_field"
            formikProps={formikProps}
            name="role"
            options={rolesOptions}
            placeholder="Search Role"
            title="Your Role"
            triggerErrorOnChange
          />
          {formikProps.values.organization === defaultPracticeId && (
            <FormTextInput
              className="HCPWorkflowShared-form_field"
              formikProps={formikProps}
              name="organization_name"
              placeholder="Your Organization Name"
              title="Your Organization Name"
              triggerErrorOnChange
            />
          )}
        </div>

        <div className="mt-4 fs-15 txt-gry">
          <span id="self-signup-consent">
            By clicking “Agree and Continue”, you are authorizing that the entity represented in
            Your Organization is one you are officially employed by. Any individual who is
            submitting this without a prior reason and authorized affiliation is subject to the full
            extent of the law.
          </span>
        </div>

        <div className="my-4 text-center w-100">
          <div>
            {mounted && (
              <Recaptcha
                className="mx-auto recaptcha"
                ref={reCaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                verifyCallback={async () => {
                  await formikProps.setFieldValue("recaptcha", true);
                  await formikProps.setFieldTouched("recaptcha", true);
                }}
              />
            )}
          </div>
          <div className="text-center my-2">
            {formikProps.touched?.recaptcha && formikProps.errors.recaptcha && (
              <FormFieldError>{formikProps.errors.recaptcha}</FormFieldError>
            )}
          </div>
        </div>

        <div className="d-flex tw:justify-center">
          <Button
            buttonType={ButtonTypes.primaryV2}
            className="Btn Btn--pri self-signup-btn"
            // button is disabled if the form has not been touched yet
            // or if there are errors
            disabled={!formikProps.dirty || Object.keys(formikProps.errors).length}
            type="submit"
          >
            Agree and Continue
          </Button>
        </div>
      </Form>
    );
  };

  const submitHandler = async (formData, formikProps) => {
    // fill in the correct organization name
    // if the free text was not selected
    if (formData.organization !== defaultPracticeId) {
      const organizationOption = organizationOptions.find(
        (item) => item.value === formData.organization,
      );

      formData.organization_name = organizationOption?.label ?? "";
      // reset organization name field so that if the update fails switching to
      // the other option will not cause the field to be prefilled
      formikProps.setFieldValue("organization_name", "");
    }

    const clonedFormData = _.cloneDeep(formData);

    delete clonedFormData.recaptcha;
    delete clonedFormData["email_validation"];

    const state = { hcp_info: clonedFormData, practice_id: clonedFormData["organization"] };

    await update_state_and_get_next_node(state, currentStepName);
  };

  return (
    <div className="container hcp-mobile-container px-sm-3 py-sm-5">
      <CardSecondary className="HCPWorkflowShared-card">
        <div className="HCPWorkflowShared-header_container">
          <h1 className="HCPWorkflowShared-title">Let’s get started.</h1>
          <p className="HCPWorkflowShared-sub_title">
            Tell us a bit about yourself so we know who’s making the referral.
          </p>
        </div>
        <CustomForm
          initialValues={{
            email: "",
            email_validation: "",
            first_name: "",
            last_name: "",
            organization: "",
            organization_name: "",
            recaptcha: false,
            role: "",
          }}
          onSubmit={submitHandler}
          render={renderForm}
          validationSchema={getHCPValidation(defaultPracticeId)}
        />
      </CardSecondary>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
  };
};

export default connect(mapStateToProps, { showAlertWithAction, showAlertWithCustomHTML })(HCPInfo);

const getHCPValidation = (defaultPracticeId) =>
  yup.object().shape({
    email: yup.string().email("Enter a valid email address").required("Email is required"),
    email_validation: yup
      .string()
      .email("Enter a valid email address")
      .required("Confirm Email is required")
      .test("email-match", "Email address does not match, please check spelling", function (value) {
        if (!value) {
          return false;
        }

        return this.parent.email === value;
      }),
    first_name: yup
      .string()
      .required("Your First Name is required")
      .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
    last_name: yup
      .string()
      .required("Your Last Name is required")
      .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
    organization: yup.string().required("Your Organization is required"),
    organization_name: yup.string().when("organization", {
      is: defaultPracticeId,
      then: yup
        .string()
        .required("Organization Name is required")
        .test("organization_name", "Organization Name Validation", (value) => {
          return Boolean(value);
        }),
    }),
    recaptcha: yup.boolean().oneOf([true], "reCAPTCHA is required"),
    role: yup.string().required("Your Role is required"),
  });

const rolesOptions = [
  { label: "Administrator", value: "Administrator" },
  { label: "aptihealth Care Navigator", value: "aptihealth Care Navigator" },
  {
    label: "aptihealth Patient Engagement Specialist",
    value: "aptihealth Patient Engagement Specialist",
  },
  { label: "Care Manager", value: "Care Manager" },
  { label: "Case Manager", value: "Case Manager" },
  { label: "Crisis Team Member", value: "Crisis Team Member" },
  { label: "Discharge Coordinator", value: "Discharge Coordinator" },
  { label: "Medical Assistant", value: "Medical Assistant" },
  { label: "Medical Provider (MD, DO, NP, PA)", value: "Medical Provider (MD, DO, NP, PA)" },
  { label: "Nurse (RN, LPN)", value: "Nurse (RN, LPN)" },
  { label: "Psychiatrist", value: "Psychiatrist" },
  { label: "Psychologist", value: "Psychologist" },
  { label: "Social Worker", value: "Social Worker" },
  { label: "Other", value: "Other" },
];
